// taken from the simple sortable example on the React DnD docs
// https://react-dnd.github.io/react-dnd/examples/sortable/simple

import { useCallback } from 'react';
import update from 'immutability-helper';
import { useDrag, useDrop } from 'react-dnd';

export interface IDragHistoryItem {
  id: string;
  endIndex: number;
}

export function useDragAndDropContainer(
  rearrangedList: any[],
  setRearrangedList: (list: any) => void,
  addToDnDHistory: (history: IDragHistoryItem) => void,
) {
  const moveItem = useCallback(
    (id: string, hoverIndex: number) => {
      addToDnDHistory({ id, endIndex: hoverIndex });
      const dragIndex = rearrangedList.findIndex((item) => item.id === id);
      const dragCard = rearrangedList[dragIndex];
      setRearrangedList(
        update(rearrangedList, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragCard],
          ],
        }),
      );
    },
    [rearrangedList],
  );

  const findItemIndex = useCallback(
    (id: string) => rearrangedList.findIndex((item) => item.id === id),
    [rearrangedList],
  );

  return {
    moveItem,
    findItemIndex,
  };
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export function useDragAndDropComponent(
  listName: string,
  index: number,
  id: string,
  moveItem: ((id: string, hoverIndex: number) => void) | undefined,
  findItemIndex: ((id: string) => number) | undefined,
) {
  const [{ handlerId }, drop] = useDrop({
    accept: `${listName}_item`,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover({ id: draggedId }: DragItem) {
      if (draggedId !== id) {
        const index = findItemIndex?.(id);
        moveItem?.(draggedId, index || 0);
      }
    },
  });

  const [, drag] = useDrag({
    type: `${listName}_item`,
    item: () => ({
      id,
      index,
    }),
    collect: (monitor: any) => ({
      getItem: monitor.getItem(),
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      const { id: droppedId, index } = item;
      const didDrop = monitor.didDrop();
      if (!didDrop) {
        moveItem?.(droppedId, index);
      }
    },
  });

  return {
    drag,
    drop,
    handlerId,
  };
}
